/**
 * Message Reactions Component
 * Quick emoji reactions for messages (❤️ 😊 👍 😂 😮 😢)
 */

import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

// Available reactions
export const REACTIONS = [
  { emoji: '❤️', name: 'love', label: 'Love' },
  { emoji: '👍', name: 'like', label: 'Like' },
  { emoji: '😊', name: 'happy', label: 'Happy' },
  { emoji: '😂', name: 'laugh', label: 'Laugh' },
  { emoji: '😮', name: 'wow', label: 'Wow' },
  { emoji: '😢', name: 'sad', label: 'Sad' },
];

/**
 * Reaction Picker - Shows when long-pressing or hovering a message
 */
export const ReactionPicker = ({ 
  onSelect, 
  onClose, 
  position = 'top',
  currentReaction = null 
}) => {
  const pickerRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <motion.div
      ref={pickerRef}
      initial={{ opacity: 0, scale: 0.8, y: position === 'top' ? 10 : -10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.8 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      className={`absolute ${position === 'top' ? 'bottom-full mb-2' : 'top-full mt-2'} 
        left-0 z-50 bg-white rounded-full shadow-lg border border-gray-100 
        px-2 py-1.5 flex items-center gap-1`}
    >
      {REACTIONS.map((reaction, index) => (
        <motion.button
          key={reaction.name}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: index * 0.03 }}
          whileHover={{ scale: 1.3 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => {
            onSelect(reaction);
            onClose?.();
          }}
          className={`w-9 h-9 flex items-center justify-center rounded-full 
            hover:bg-gray-100 transition-colors text-xl
            ${currentReaction === reaction.name ? 'bg-blue-100' : ''}`}
          title={reaction.label}
        >
          {reaction.emoji}
        </motion.button>
      ))}
    </motion.div>
  );
};

/**
 * Reaction Display - Shows reactions under a message
 */
export const ReactionDisplay = ({ 
  reactions = [], 
  onReactionClick,
  currentUserId,
  isOwnMessage = false 
}) => {
  if (!reactions || reactions.length === 0) return null;

  // Group reactions by emoji
  const groupedReactions = reactions.reduce((acc, r) => {
    if (!acc[r.reaction]) {
      acc[r.reaction] = { count: 0, users: [], hasCurrentUser: false };
    }
    acc[r.reaction].count++;
    acc[r.reaction].users.push(r.user_id);
    if (r.user_id === currentUserId) {
      acc[r.reaction].hasCurrentUser = true;
    }
    return acc;
  }, {});

  const reactionEntries = Object.entries(groupedReactions);

  return (
    <motion.div 
      initial={{ opacity: 0, y: -5 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex flex-wrap gap-1 mt-1 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
    >
      {reactionEntries.map(([reactionName, data]) => {
        const reactionObj = REACTIONS.find(r => r.name === reactionName);
        if (!reactionObj) return null;

        return (
          <motion.button
            key={reactionName}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onReactionClick?.(reactionObj)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs
              transition-all duration-200
              ${data.hasCurrentUser 
                ? 'bg-blue-100 border border-blue-300 text-blue-700' 
                : 'bg-gray-100 border border-gray-200 text-gray-600 hover:bg-gray-200'
              }`}
            title={`${data.users.length} ${reactionObj.label}`}
          >
            <span className="text-sm">{reactionObj.emoji}</span>
            {data.count > 1 && <span className="font-medium">{data.count}</span>}
          </motion.button>
        );
      })}
    </motion.div>
  );
};

/**
 * Reaction Button - Small button to add reaction to a message
 */
export const ReactionButton = ({ onClick, className = '' }) => (
  <motion.button
    whileHover={{ scale: 1.1 }}
    whileTap={{ scale: 0.9 }}
    onClick={onClick}
    className={`p-1 rounded-full hover:bg-gray-100 text-gray-400 
      hover:text-gray-600 transition-colors ${className}`}
    title="Add reaction"
  >
    <svg 
      className="w-4 h-4" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
      />
    </svg>
  </motion.button>
);

/**
 * Floating Reaction Animation - Shows when someone reacts
 */
export const FloatingReaction = ({ emoji, onComplete }) => (
  <motion.div
    initial={{ opacity: 1, scale: 0, y: 0 }}
    animate={{ 
      opacity: [1, 1, 0],
      scale: [0, 1.5, 1],
      y: -50 
    }}
    transition={{ duration: 0.8, ease: 'easeOut' }}
    onAnimationComplete={onComplete}
    className="absolute -top-2 left-1/2 -translate-x-1/2 text-2xl pointer-events-none z-50"
  >
    {emoji}
  </motion.div>
);

/**
 * Hook to manage reactions for a message
 */
export const useMessageReactions = (messageId, initialReactions = []) => {
  const [reactions, setReactions] = useState(initialReactions);
  const [showPicker, setShowPicker] = useState(false);

  const addReaction = (reaction, userId) => {
    setReactions(prev => {
      // Check if user already reacted with this emoji
      const existingIndex = prev.findIndex(
        r => r.user_id === userId && r.reaction === reaction.name
      );
      
      if (existingIndex >= 0) {
        // Remove reaction (toggle off)
        return prev.filter((_, i) => i !== existingIndex);
      }
      
      // Remove any other reaction by this user first
      const filtered = prev.filter(r => r.user_id !== userId);
      
      // Add new reaction
      return [...filtered, {
        message_id: messageId,
        user_id: userId,
        reaction: reaction.name,
        created_at: new Date().toISOString()
      }];
    });
  };

  const removeReaction = (userId) => {
    setReactions(prev => prev.filter(r => r.user_id !== userId));
  };

  const getUserReaction = (userId) => {
    const userReaction = reactions.find(r => r.user_id === userId);
    return userReaction ? REACTIONS.find(r => r.name === userReaction.reaction) : null;
  };

  return {
    reactions,
    setReactions,
    showPicker,
    setShowPicker,
    addReaction,
    removeReaction,
    getUserReaction,
  };
};

export default {
  REACTIONS,
  ReactionPicker,
  ReactionDisplay,
  ReactionButton,
  FloatingReaction,
  useMessageReactions,
};
